import { type RefObject, useEffect, useLayoutEffect } from 'react';
import { WEB_STAGE_CONFIG } from './hero-stage/stage-config';

// useLayoutEffect warns during SSR; fall back to useEffect on the server.
const useIsoLayoutEffect =
	typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// Width-driven uniform zoom that mirrors the hero canvas's smooth scale band, so
// flow-layout sections (Unlock / FAQ) + the apply page "zoom" with the viewport
// the same way the pinned hero stage does — content grows toward the 1440 design
// width, keeps scaling UP to maxScaleAtWidth/refWidth on bigger screens (matching
// the hero), and shrinks toward a floor on narrower screens.
//
//   zoom = clamp(minScaleAtWidth/refWidth, vw/refWidth, maxScaleAtWidth/refWidth)
//
// Applied as CSS `zoom` (NOT transform) so the element stays layout-aware: its
// scaled height feeds normal document flow (scroll height stays correct) and any
// sticky ancestors are unaffected — a transform would break both. Attach the ref
// to the section's INNER content wrapper, never the full-bleed <section>: zoom
// scales width too, so zooming the section root would shrink its background off
// the viewport edge; zooming only the centered content keeps the bg full-bleed.
//
// Mirrors useStageCanvas's JS-on-resize model (the canvas can't express vw/ref as
// a unitless ratio in pure CSS calc, so the factor is computed here).
// `minWidth` (optional): below this viewport width the zoom is cleared (1) — for
// elements SHARED by mobile, where the 1440-web curve must not shrink the mobile
// layout (e.g. the full-screen ManageHero). Omit for web-only sections.
export function useFluidZoom(
	ref: RefObject<HTMLElement | null>,
	opts?: { minWidth?: number },
): void {
	const minWidth = opts?.minWidth ?? 0;
	useIsoLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;
		const { refWidth, minScaleAtWidth, maxScaleAtWidth } =
			WEB_STAGE_CONFIG.canvas;
		const floor = minScaleAtWidth / refWidth;
		// Ceiling mirrors the hero canvas's maxScaleAtWidth (currently 1.333× at
		// 1920) so flow sections + the apply page scale UP on big screens in
		// lockstep with the hero — not capped at native while the hero grows. Stays
		// in sync automatically if maxScaleAtWidth changes (e.g. reverted to 1440).
		const ceil = maxScaleAtWidth / refWidth;
		const apply = () => {
			const vw = document.documentElement.clientWidth;
			if (vw < minWidth) {
				// below the gate (mobile) — no zoom, let the native responsive layout
				// stand. Clear rather than set 1 so nothing lingers in inline style.
				el.style.removeProperty('zoom');
				return;
			}
			const zoom = Math.min(ceil, Math.max(floor, vw / refWidth));
			el.style.zoom = String(zoom);
		};
		apply();
		window.addEventListener('resize', apply);
		return () => window.removeEventListener('resize', apply);
	}, [ref, minWidth]);
}

// Pre-paint counterpart to useFluidZoom: an inline script that applies the SAME
// zoom factor SYNCHRONOUSLY in the SSR HTML, before the browser paints — so a
// server-rendered section (e.g. the APPROVED Manage Card hero) is already scaled
// on the first frame instead of painting at native size then snapping when the
// useLayoutEffect runs on hydration. Render it immediately AFTER the zoom target:
// it finds that target via document.currentScript.previousElementSibling and
// mirrors useFluidZoom's clamp(floor, vw/refWidth, ceil) EXACTLY (keep in sync).
// `minWidth` matches the hook: below it the zoom is cleared (mobile). Wrapped so
// a throw can never block render; the hook then re-asserts + tracks resize.
export function fluidZoomPrePaintScript(minWidth = 0): string {
	const { refWidth, minScaleAtWidth, maxScaleAtWidth } =
		WEB_STAGE_CONFIG.canvas;
	const floor = minScaleAtWidth / refWidth;
	const ceil = maxScaleAtWidth / refWidth;
	return `(function(){try{
var c=document.currentScript;var el=c&&c.previousElementSibling;
if(!el)return;
var vw=document.documentElement.clientWidth;
if(vw<${minWidth}){el.style.removeProperty('zoom');return;}
var z=Math.min(${ceil},Math.max(${floor},vw/${refWidth}));
el.style.zoom=String(z);
}catch(e){}})();`;
}
